import pako from 'pako';

/**
 * Compress data using pako (gzip)
 * @param {any} data - Data to compress (will be JSON stringified)
 * @returns {string} Base64 encoded compressed data
 */
export const compressData = (data) => {
  try {
    // Convert data to JSON string
    const jsonString = JSON.stringify(data);
    
    // Convert string to Uint8Array
    const uint8Array = new TextEncoder().encode(jsonString);
    
    // Compress using pako
    const compressed = pako.gzip(uint8Array);
    
    // Convert to base64 for safe transport, in chunks: spreading a large buffer into one
    // String.fromCharCode call overflows the argument limit (64K in Safari)
    let binary = '';
    for (let offset = 0; offset < compressed.length; offset += 0x8000) {
      binary += String.fromCharCode.apply(null, compressed.subarray(offset, offset + 0x8000));
    }
    const base64 = btoa(binary);
    
    // Calculate compression ratio for logging
    const originalSize = jsonString.length;
    const compressedSize = base64.length;
    const ratio = ((1 - compressedSize / originalSize) * 100).toFixed(1);
    
    console.log(`Compression: ${originalSize} bytes → ${compressedSize} bytes (${ratio}% reduction)`);
    
    return base64;
  } catch (error) {
    console.error('Compression failed:', error);
    throw error;
  }
};

/**
 * Decompress base64 encoded gzipped data
 * @param {string} compressedData - Base64 encoded compressed data
 * @returns {any} Decompressed and parsed data
 */
export const decompressData = (compressedData) => {
  try {
    // Convert base64 to binary
    const binaryString = atob(compressedData);
    
    // Convert binary string to Uint8Array
    const uint8Array = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      uint8Array[i] = binaryString.charCodeAt(i);
    }
    
    // Decompress using pako
    const decompressed = pako.ungzip(uint8Array);
    
    // Convert Uint8Array to string
    const jsonString = new TextDecoder().decode(decompressed);
    
    // Parse JSON
    const data = JSON.parse(jsonString);
    
    console.log(`Decompression: ${compressedData.length} bytes → ${jsonString.length} bytes`);
    
    return data;
  } catch (error) {
    console.error('Decompression failed:', error);
    throw error;
  }
};

/**
 * Check if compression would be beneficial
 * @param {Array} data - Array to check
 * @returns {boolean} Whether compression should be used
 */
export const shouldCompress = (data) => {
  // Use compression for arrays with more than 100 items
  // or if the JSON string would be larger than 10KB
  if (!Array.isArray(data)) return false;
  
  if (data.length > 100) return true;
  
  const estimatedSize = JSON.stringify(data).length;
  return estimatedSize > 10240; // 10KB
};

/**
 * Prepare payload with optional compression
 * @param {Array} userIds - Array of user IDs
 * @returns {Object} Payload with userIds (compressed or not)
 */
export const prepareUserIdsPayload = (userIds) => {
  if (shouldCompress(userIds)) {
    return {
      userIdsCompressed: compressData(userIds),
      compressionType: 'gzip',
      originalCount: userIds.length
    };
  }
  
  // Return uncompressed for small lists
  return {
    userIds
  };
};
